var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, forwardRef } from 'react';
import { Listbox } from '@headlessui/react';
import { usePopper } from 'react-popper';
import DropdownContext from './private/utils/DropdownContext';
import useDropdownContext from './private/utils/useDropdownContext';
import useFormContext from '../form/private/utils/useFormContext';
import useFormItemContext from '../form/private/utils/useFormItemContext';
import GenericHint from '../hint/Hint';
import { SelectButton } from '../index';
import mergeClassnames from '../mergeClassnames/mergeClassnames';
const DropdownRoot = (_a) => {
    var { children, value, onChange, isError: dropdownError, disabled: dropdownDisabled, size: dropdownSize = 'md', className, onClear, position = 'bottom-start' } = _a, rest = __rest(_a, ["children", "value", "onChange", "isError", "disabled", "size", "className", "onClear", "position"]);
    const [anchorEl, setAnchorEl] = React.useState();
    const [popperEl, setPopperEl] = React.useState();
    const { size: formSize } = useFormContext('Input');
    const { size: formItemSize, disabled: formItemDisabled, error: formItemError, } = useFormItemContext('Input');
    const size = dropdownSize || formItemSize || formSize;
    const disabled = dropdownDisabled || formItemDisabled;
    const isError = dropdownError || formItemError;
    let { styles, attributes } = usePopper(anchorEl, popperEl, {
        placement: position,
    });
    const states = {
        value: value,
        isError: isError,
        size: size,
        disabled: disabled,
        onClear: onClear,
        pooper: {
            styles: styles,
            attributes: attributes,
            setAnchor: setAnchorEl,
            setPopper: setPopperEl,
        },
    };
    const childrens = typeof children !== 'function' ? React.Children.toArray(children) : [];
    const callableChildren = typeof children === 'function' && children;
    return (React.createElement(DropdownContext.Provider, { value: states },
        React.createElement("div", { className: mergeClassnames('w-full relative', className && className) },
            React.createElement(Listbox, Object.assign({ value: value, onChange: onChange, disabled: disabled }, rest), ({ open }) => (React.createElement("div", { className: "relative" }, typeof children === 'function'
                ? callableChildren && callableChildren({ open })
                : childrens === null || childrens === void 0 ? void 0 : childrens.map((ch) => ch)))))));
};
const HiddenInput = forwardRef((_a, ref) => {
    var props = __rest(_a, []);
    const { value: innerValue } = useDropdownContext('Dropdown.Select');
    const currentValue = (props === null || props === void 0 ? void 0 : props.value) || innerValue;
    return React.createElement("input", Object.assign({ ref: ref, hidden: true }, props, { value: currentValue }));
});
const Options = (_a) => {
    var _b, _c;
    var { children, menuWidth, className } = _a, rest = __rest(_a, ["children", "menuWidth", "className"]);
    const { pooper } = useDropdownContext('Dropdown.Options');
    return (React.createElement(Listbox.Options, Object.assign({ ref: pooper === null || pooper === void 0 ? void 0 : pooper.setPopper, style: (_b = pooper === null || pooper === void 0 ? void 0 : pooper.styles) === null || _b === void 0 ? void 0 : _b.popper }, (_c = pooper === null || pooper === void 0 ? void 0 : pooper.attributes) === null || _c === void 0 ? void 0 : _c.popper, { className: mergeClassnames(menuWidth ? menuWidth : 'w-full min-w-[18.75rem]', 'z-5 absolute p-1 my-2 rounded-moon-s-md box-border bg-goku shadow-moon-lg overflow-y-auto focus:outline-none', className && className) }, rest), children));
};
const Option = ({ children, value }) => {
    return (React.createElement(Listbox.Option, { as: "span", value: value }, ({ selected, active }) => typeof children === 'function'
        ? children({ selected, active })
        : children));
};
const Select = (_a) => {
    var { open, value, label, placeholder, children, className } = _a, rest = __rest(_a, ["open", "value", "label", "placeholder", "children", "className"]);
    const { size, pooper, isError, disabled } = useDropdownContext('Dropdown.Select');
    return (React.createElement(React.Fragment, null,
        label && (React.createElement(SelectButton.Label, { labelSize: size, idDisabled: disabled }, label)),
        React.createElement(Listbox.Button, { as: Fragment },
            React.createElement(SelectButton, Object.assign({ size: size, open: open, isError: isError, idDisabled: disabled, ref: pooper === null || pooper === void 0 ? void 0 : pooper.setAnchor }, rest),
                React.createElement(SelectButton.Input, { className: className }, children ? (React.createElement(SelectButton.Value, null, children)) : (React.createElement(SelectButton.Placeholder, null, placeholder)))))));
};
//Dropdown.InsetSelect
const InsetSelect = (_a) => {
    var { open, value, label, placeholder, children, className } = _a, rest = __rest(_a, ["open", "value", "label", "placeholder", "children", "className"]);
    const { size, pooper, isError, disabled } = useDropdownContext('Dropdown.InsetSelect');
    return (React.createElement(Listbox.Button, { as: Fragment },
        React.createElement(SelectButton, Object.assign({ size: size, open: open, isError: isError, idDisabled: disabled, ref: pooper === null || pooper === void 0 ? void 0 : pooper.setAnchor }, rest),
            React.createElement(SelectButton.InsetInput, { className: className },
                React.createElement("span", { className: "flex flex-col items-start truncate" },
                    React.createElement(SelectButton.FloatingLabel, null, label),
                    children ? (React.createElement(SelectButton.Value, null, children)) : (React.createElement(SelectButton.Placeholder, null, placeholder)))))));
};
//Dropdown.MultiSelect
const MultiSelect = (_a) => {
    var { open, value, label, placeholder, children, className, counter = 0 } = _a, rest = __rest(_a, ["open", "value", "label", "placeholder", "children", "className", "counter"]);
    const { size, pooper, isError, disabled, onClear } = useDropdownContext('Dropdown.MultiSelect');
    return (React.createElement(React.Fragment, null,
        label && (React.createElement(SelectButton.Label, { labelSize: size, idDisabled: disabled }, label)),
        React.createElement(Listbox.Button, { as: Fragment },
            React.createElement(SelectButton, Object.assign({ size: size, open: open, isError: isError, idDisabled: disabled, ref: pooper === null || pooper === void 0 ? void 0 : pooper.setAnchor }, rest),
                React.createElement(SelectButton.Input, { className: mergeClassnames(className) },
                    React.createElement("span", { className: "flex w-full gap-2 items-center" },
                        counter > 0 && (React.createElement(SelectButton.Chip, { onClear: onClear }, counter)),
                        React.createElement(SelectButton.Placeholder, null, placeholder)))))));
};
//Dropdown.InsetMultiSelect
const InsetMultiSelect = (_a) => {
    var { open, value, label, placeholder, children, className, counter = 0 } = _a, rest = __rest(_a, ["open", "value", "label", "placeholder", "children", "className", "counter"]);
    const { size, pooper, isError, disabled, onClear } = useDropdownContext('Dropdown.InsetMultiSelect');
    return (React.createElement(Listbox.Button, { as: Fragment },
        React.createElement(SelectButton, Object.assign({ size: size, open: open, isError: isError, idDisabled: disabled, ref: pooper === null || pooper === void 0 ? void 0 : pooper.setAnchor }, rest),
            React.createElement(SelectButton.InsetInput, { className: mergeClassnames(className, '[&_>_span]:gap-4') },
                counter > 0 && (React.createElement(SelectButton.Chip, { onClear: onClear }, counter)),
                React.createElement("span", { className: "flex flex-col items-start truncate" },
                    React.createElement(SelectButton.FloatingLabel, null, label),
                    React.createElement(SelectButton.Placeholder, null, placeholder))))));
};
//Dropdown.Trigger
const Trigger = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { pooper } = useDropdownContext('Dropdown.Trigger');
    return (React.createElement(Listbox.Button, Object.assign({ ref: pooper === null || pooper === void 0 ? void 0 : pooper.setAnchor, className: className && className }, rest), children));
};
//Dropdown.Hint
const Hint = ({ children, className, }) => {
    const { isError, disabled } = useDropdownContext('Dropdown.Input');
    return (React.createElement(GenericHint, { error: isError, disabled: disabled, className: className }, children));
};
//Dropdown
const Dropdown = Object.assign(DropdownRoot, {
    Options,
    Option,
    Trigger,
    Hint,
    Select,
    InsetSelect,
    MultiSelect,
    InsetMultiSelect,
    HiddenInput,
});
export default Dropdown;
//TODO: Remove SelectButton.Label and and use Label
